/**
 * VIRAL MOMENT ENGINE™
 *
 * Pure, dependency-free heuristic that scores how viral a live match moment
 * is likely to be. Used by cinematic cards, auto-capsules, and the share
 * sheet to surface "viral potential" badges and route creators to the right
 * platform.
 *
 * No DB calls, no React — safe to import anywhere.
 */

export type ViralPlatform = "tiktok" | "instagram" | "x" | "whatsapp" | "telegram" | "facebook";

export interface ViralMomentInput {
  kind?: string | null;        // e.g. "goal", "penalty", "last_minute", "rivalry"
  minute?: number | null;      // current match minute
  isGoal?: boolean;
  isRivalry?: boolean;
  isComeback?: boolean;
  isPenalty?: boolean;
  isRedCard?: boolean;
  hypeIndex?: number | null;   // 0..100 — current crowd hype
  emotion?: string | null;     // "epic" | "shock" | "joy" | ...
}

export interface ViralScore {
  viralScore: number;          // 0..100
  sharePotential: "low" | "medium" | "high" | "explosive";
  creatorOpportunity: "skip" | "good" | "great" | "now";
  recommendedPlatform: ViralPlatform;
  label: string;               // human-readable badge label
}

const EMO_BOOST: Record<string, number> = {
  epic: 18, shock: 16, joy: 12, anger: 10, awe: 14, drama: 15,
};

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n));
}

export function scoreMoment(input: ViralMomentInput): ViralScore {
  let s = 10;
  if (input.isGoal) s += 40;
  if (input.isPenalty) s += 25;
  if (input.isRedCard) s += 22;
  if (input.isRivalry) s += 20;
  if (input.isComeback) s += 15;
  if (typeof input.minute === "number" && input.minute >= 85) s += 18;
  if (typeof input.hypeIndex === "number") s += clamp(input.hypeIndex, 0, 100) * 0.2;
  if (input.emotion && EMO_BOOST[input.emotion.toLowerCase()]) {
    s += EMO_BOOST[input.emotion.toLowerCase()];
  }
  // Kind-based fallback if booleans absent
  const k = (input.kind ?? "").toLowerCase();
  if (!input.isGoal && k.includes("goal")) s += 35;
  if (k.includes("last_minute") || k.includes("last-minute")) s += 18;
  if (k.includes("rivalry") || k.includes("nation")) s += 12;

  const viralScore = Math.round(clamp(s));
  const sharePotential: ViralScore["sharePotential"] =
    viralScore >= 85 ? "explosive" : viralScore >= 65 ? "high" : viralScore >= 40 ? "medium" : "low";
  const creatorOpportunity: ViralScore["creatorOpportunity"] =
    viralScore >= 85 ? "now" : viralScore >= 65 ? "great" : viralScore >= 40 ? "good" : "skip";

  // Platform routing: explosive→TikTok/IG reels; rivalry/text→X; chat→WA/TG
  let recommendedPlatform: ViralPlatform = "tiktok";
  if (input.isRivalry || k.includes("nation")) recommendedPlatform = "x";
  else if (viralScore >= 85) recommendedPlatform = "tiktok";
  else if (viralScore >= 65) recommendedPlatform = "instagram";
  else recommendedPlatform = "whatsapp";

  const label =
    sharePotential === "explosive" ? "VIRAL EXPLOSIVO"
    : sharePotential === "high" ? "ALTO POTENCIAL VIRAL"
    : sharePotential === "medium" ? "POTENCIAL VIRAL"
    : "MOMENTO REGULAR";

  return { viralScore, sharePotential, creatorOpportunity, recommendedPlatform, label };
}

/** React hook wrapper for components. */
export function useViralScore(input: ViralMomentInput): ViralScore {
  return scoreMoment(input);
}